import { Observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import Swipe from 'react-easy-swipe';

import { Loading } from 'src/components/loading';
import { Modal } from 'src/components/modal';
import { closeConfirmModal, onConfirm } from 'src/modules/transferRegistration/actions';
import {
  Wrapper,
  DraggableStroke,
  Header,
  StyledDivider,
  Content,
  Controls,
  ScrollBarWrapper,
} from 'src/modules/transferRegistration/components/styled';
import { PRELOADER, LOADER } from 'src/modules/transferRegistration/constants';
import { modalStore } from 'src/modules/transferRegistration/stores';
import { ModalStoreType } from 'src/modules/transferRegistration/types';
import { windowSize } from 'src/stores';
import { Button } from 'src/styledComponents/Button';
import { ShowMobile } from 'src/styledComponents/Conditions';
import { blockBodyScroll, t, unblockBodyScroll } from 'src/utils';

import { BlockInfo, BlockRecipientInfo, BlockSubtitle } from './Fields';
import { Title } from './Title';

type Props = {
  open: boolean;
};

const ConfirmModal = ({ open }: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isSwipeEnd, setIsSwipeEnd] = useState(false);

  const handleMove = ({ y: value }: { y: number }) => {
    if (!ref.current || value < 0) return;

    ref.current.style.setProperty('transform', `translate3d(0, ${value}px, 0)`);
    return true;
  };

  const handleSwipeDown = () => {
    if (!ref.current) return;

    ref.current.style.setProperty('transform', `translate3d(0, ${ref.current.clientHeight}px, 0)`);
    setIsSwipeEnd(true);
  };

  const handleSwipeStart = () => {
    setIsSwipeEnd(false);
  };

  const handleTransitionEnd = () => {};

  useEffect(() => {
    blockBodyScroll();
    return () => {
      unblockBodyScroll();
    };
  }, []);

  return (
    <TestAnchorContext.Provider value={{ container: 'Transfer-Registration-Popup-Form' }}>
      <Observer>
        {() => {
          const isMobile = windowSize.isLessThan('large');
          const { name, email, distanceName, recipientEmail } = modalStore.value as ModalStoreType;

          return (
            <Modal open={open}>
              <Wrapper ref={ref} isSwipeEnd={isSwipeEnd} onTransitionEnd={handleTransitionEnd} isDesktop={!isMobile} maxHeight={715}>
                <ScrollBarWrapper isDesktop={!isMobile}>
                  <ShowMobile>
                    <Swipe className='swipe-zone' onSwipeMove={handleMove} onSwipeDown={handleSwipeDown} onSwipeStart={handleSwipeStart}>
                      <DraggableStroke />
                    </Swipe>
                  </ShowMobile>
                  <Loading action={PRELOADER} opacity={1} />
                  <Loading action={LOADER} opacity={1} />

                  <Header>
                    <Title isMobile={isMobile} title={t.staticAsString('transferRegistration.form.title')} />
                  </Header>

                  <StyledDivider light />

                  <Content>
                    <BlockRecipientInfo text='transferRegistration.confirm.p2' isNeedPadding />
                    <BlockRecipientInfo text='transferRegistration.confirm.p' isNeedPadding={false} />

                    <BlockSubtitle title='transferRegistration.form.registrantInfo' />
                    <BlockInfo title='transferRegistration.form.name' value={name} />
                    <BlockInfo title='transferRegistration.form.email' value={email} />
                    <BlockInfo title='transferRegistration.form.distance' value={distanceName} />

                    <BlockSubtitle title='transferRegistration.form.recipientInfo' />
                    <BlockInfo title='transferRegistration.form.email' value={recipientEmail} />
                  </Content>

                  <Controls>
                    <Button
                      variant='secondary'
                      size='medium'
                      ATN='cancel'
                      onClick={closeConfirmModal}
                      className={isMobile ? 'margin-right' : ''}
                    >
                      {t.staticAsString('transferRegistration.confirm.reject')}
                    </Button>

                    <Button variant='primary' size='medium' fullWidth={isMobile} ATN='submit' onClick={onConfirm}>
                      {t.staticAsString('transferRegistration.confirm.confirm')}
                    </Button>
                  </Controls>
                </ScrollBarWrapper>
              </Wrapper>
            </Modal>
          );
        }}
      </Observer>
    </TestAnchorContext.Provider>
  );
};

export { ConfirmModal };
